import React, { useEffect } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate } from "react-router-dom";
import {
  FormBody,
  FormContentContainer,
  BottomActionButton,
  SecondaryActionButton,
} from "@components";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import { useBankTransferAttempts } from "@hooks";
import { onContactUsClick } from "@utils/contactUs";
import { BANK_TRANSFER_ATTEMPTS_LIMIT, failedBankTransferWsp } from "@constants/index";
import { tracker } from "@utils/openreplay";
import { Routes } from "@utils";

const StyledBottomActionButton = styled(BottomActionButton)({
  gap: 6,
});

const StyledCancelIcon = styled(CancelOutlined)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.error.main,
}));

const HeadingBox = styled(Box)({
  marginBottom: "48px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
});

const ExplanationTypography = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: "23px",
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ContactLink = styled("button")(({ theme }) => ({
  padding: 0,
  border: "none",
  background: "none",
  font: "inherit",
  fontWeight: 500,
  color: theme.palette.text.link,
  cursor: "pointer",
}));

interface Props {
  source: string;
  onRetry: () => void;
  isRetrying: boolean;
}

export const BankTransferFailureScreen: React.FC<Props> = ({
  source,
  onRetry,
  isRetrying,
}) => {
  const navigate = useNavigate();
  const { hasExhaustedAttempts } = useBankTransferAttempts();

  const contactSupport = () => {
    tracker?.event("BankTransferSupportRequested", {
      source,
      hasExhaustedAttempts,
    });
    onContactUsClick(failedBankTransferWsp);
  };

  useEffect(() => {
    if (!hasExhaustedAttempts) return;
    tracker?.event("BankTransferAttemptsExhausted", { source });
  }, [hasExhaustedAttempts, source]);

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <HeadingBox>
            <StyledCancelIcon />
            <Typography variant="h5" fontWeight="semibold" textAlign="center">
              {hasExhaustedAttempts
                ? "No pudimos verificar tu identidad"
                : "Transferencia cancelada"}
            </Typography>
            <ExplanationTypography>
              {hasExhaustedAttempts ? (
                <>
                  Después de {BANK_TRANSFER_ATTEMPTS_LIMIT} intentos no logramos completar
                  la verificación. Escríbenos y lo resolvemos contigo.
                </>
              ) : (
                <>
                  Puedes intentar nuevamente, o{" "}
                  <ContactLink type="button" onClick={contactSupport}>
                    contáctanos
                  </ContactLink>{" "}
                  y te ayudamos.
                </>
              )}{" "}
              Si alcanzaste a transferir, el monto se reembolsa automáticamente.
            </ExplanationTypography>
          </HeadingBox>

          {hasExhaustedAttempts ? (
            <StyledBottomActionButton onClick={contactSupport}>
              Contactar a asistencia
            </StyledBottomActionButton>
          ) : (
            <StyledBottomActionButton onClick={onRetry} disabled={isRetrying}>
              Intentar nuevamente
              {isRetrying ? <CircularProgress size={20} color="inherit" /> : null}
            </StyledBottomActionButton>
          )}

          <SecondaryActionButton
            onClick={() => navigate(Routes.SOLICITUDES)}
            disabled={isRetrying}
          >
            Volver a inicio
          </SecondaryActionButton>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
